import { GOVERNANCE_AGREEMENT_VERSION, PARTNER_ORGANISATION } from './constants.js';

/**
 * Sole-practitioner prescribing environment agreement governing use of the
 * ScriptPad platform when issuing electronic prescriptions outside of a
 * configured healthcare provider organisation context.
 *
 * NOTE: This is a generated draft for use during onboarding. It should be
 * reviewed and approved by ScriptPad's legal counsel before relying on it
 * in production.
 */
export const GOVERNANCE_AGREEMENT = {
  version: GOVERNANCE_AGREEMENT_VERSION,
  organisationName: PARTNER_ORGANISATION.name,
  effectiveDate: '1 March 2026',
  title: 'Prescribing Environment Acknowledgement and Use Agreement',
  sections: [
    {
      heading: '1. Parties and purpose',
      body: `This agreement is entered into between you (the "Prescriber"), being an individual healthcare provider holding current registration with the Australian Health Practitioner Regulation Agency (AHPRA) and an active Healthcare Provider Identifier \u2013 Individual (HPI-I), and ${PARTNER_ORGANISATION.name} (the "Organisation"), a registered healthcare provider organisation.

The purpose of this agreement is to govern the Prescriber's use of the ScriptPad platform when issuing electronic prescriptions within a clinical service environment supported by the Organisation, where such prescribing context is appropriate to the care being provided.`,
    },
    {
      heading: '2. Use of prescribing environment',
      body: `The Prescriber may utilise this prescribing environment where they are not operating within a configured healthcare provider organisation (such as a hospital or clinic system) and where the selected context accurately reflects the clinical setting in which care is being delivered.

Where the Prescriber is practising within a healthcare provider organisation that has its own prescribing configuration, that organisation's prescribing context should be used.

The Prescriber acknowledges that they are responsible for ensuring that prescriptions issued accurately reflect the context of care.`,
    },
    {
      heading: '3. Scope of practice',
      body: `The Prescriber shall only issue prescriptions within their scope of practice as defined by their AHPRA registration, endorsements, and any conditions imposed on that registration.`,
    },
    {
      heading: '4. Prescribing standards',
      body: `The Prescriber agrees to comply with all applicable prescribing requirements, including:

\u2022 Pharmaceutical Benefits Scheme (PBS) requirements (where applicable)
\u2022 Real Time Prescription Monitoring (RTPM) obligations
\u2022 Therapeutic Goods Administration (TGA) requirements
\u2022 Relevant professional codes of conduct`,
    },
    {
      heading: '5. Identity and credentialing',
      body: `The Prescriber consents to identity verification processes conducted through ScriptPad, including document verification and biometric matching where applicable.

The Prescriber warrants that all information provided is accurate and up to date and agrees to notify any changes to registration or authorisation status.`,
    },
    {
      heading: '6. Records and auditability',
      body: `The Prescriber is responsible for maintaining appropriate clinical records in accordance with professional and legislative requirements.

The Prescriber acknowledges that prescribing activity within ScriptPad is recorded and auditable.

The Organisation may access prescribing records for the purposes of:

\u2022 system integrity
\u2022 audit and monitoring
\u2022 complaint handling
\u2022 regulatory compliance`,
    },
    {
      heading: '7. Oversight and platform controls',
      body: `Prescribing activity conducted within the platform is subject to monitoring and review to support safe and appropriate use.

The Organisation may:

\u2022 review prescribing activity
\u2022 require additional information or clarification
\u2022 restrict or suspend access where there are reasonable concerns regarding safety or compliance`,
    },
    {
      heading: '8. Privacy and patient consent',
      body: `The Prescriber is responsible for obtaining appropriate patient consent and handling personal and health information in accordance with applicable privacy legislation.`,
    },
    {
      heading: '9. Adverse events and notifications',
      body: `The Prescriber agrees to notify relevant parties of any significant adverse events or regulatory matters as required under applicable laws and professional obligations.`,
    },
    {
      heading: '10. Insurance',
      body: `The Prescriber warrants that they maintain appropriate professional indemnity insurance in accordance with their registration requirements.`,
    },
    {
      heading: '11. Suspension and termination',
      body: `Access to the platform may be suspended or terminated where necessary to maintain patient safety, system integrity, or regulatory compliance.`,
    },
    {
      heading: '12. Governing law',
      body: `This agreement is governed by the laws of New South Wales, Australia.`,
    },
    {
      heading: '13. Acknowledgement',
      body: `By accepting this agreement, the Prescriber confirms that:

\u2022 they understand their responsibilities as a prescriber
\u2022 they will use the platform only where the prescribing context is appropriate
\u2022 they remain solely responsible for clinical decision-making`,
    },
  ],
} as const;

export type GovernanceAgreement = typeof GOVERNANCE_AGREEMENT;
